import { useState, useEffect, FormEvent } from "react";

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  registerableType?: string;
  registerableId?: number;
}

const cities = [
  "ארצי מקוון",
  "אשקלון",
  "בית שאן",
  "בת ים",
  "גוש עציון ומבואות ירושלים",
  "הר חברון קרית ארבע",
  "חדרה",
  "חריש",
  "טבריה",
  "יקנעם",
  "כפר יונה",
  "מועצה אזורית שומרון",
  "מטה יהודה",
  "מעלה אדומים",
  "מצפה רמון / רמת נגב",
  "נהריה",
  "ערד",
  "פרדס חנה אזורי",
  "קרית ביאליק",
  "קרית גת",
  "קרית מלאכי / באר טוביה",
  "קרית שמונה",
  "קרני שומרון",
  "ראש העין",
  "רעננה / הרצליה",
  "טבריה",
  "מעלה אדומים",
  "נהריה",
  "פרדס אזורי אזורי",
  "מוקד מקוון ארצי ",
  "קרית גת",
  "רמת גן",
  "טבריה",
  "נהריה",
  "מעלה אדומים",
  "קרית גת",
];

export default function RegistrationModal({
  isOpen,
  onClose,
  registerableType,
  registerableId,
}: RegistrationModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notWorking, setNotWorking] = useState(false);
  const [welfareKnown, setWelfareKnown] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [checked, setChecked] = useState(true);
  const [showMore, setShowMore] = useState(false);

  const text = `בחתימתי מטה אני מצהירה כי הוסבר לי ואני מסכימה, כי לטובת מתן השירותים במסגרת התוכנית, שמידע אישי עליי יישמר, יעשה בו שימוש ויועבר אל גורמים רלוונטיים, בהתאם למפורט להלן - המידע האמור יכול להיות כל מידע עלי או בשמי, בכתב או בדרך אחרת.

ידוע לי כי לא חלה עליי חובה חוקית לחתום על מסמך זה ולהסכים לאמור בו, אך ידוע לי שללא הסכמתי לא אוכל לקבל את השירותים במסגרת התוכנית.

ידוע לי כי השירות ניתן לי, למעשה, ממשרד הרווחה באמצעות העמותה, ולכן המידע גם יועבר אל המשרד, בהתאם לצורך ובסוף ההתקשרות עם העמותה או בסיום התוכנית, ויישמר במערכות המידע של המשרד ברמה המאובטחת הגבוהה ביותר, לפי דין.`;

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setLoading(false);
      setNotWorking(false);
      setWelfareKnown("");
      setPhoneError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPhoneError("");

    const form = e.currentTarget;
    const name = (form.elements.namedItem("name") as HTMLInputElement)?.value;
    const phone = (form.elements.namedItem("phone") as HTMLInputElement)?.value;
    const city = (form.elements.namedItem("city") as HTMLSelectElement)?.value;
    const salary = (form.elements.namedItem("salary") as HTMLSelectElement)
      ?.value;

    if (!name || !name.trim()) {
      return;
    }

    const cleanPhone = phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length > 11) {
      setPhoneError("מספר טלפון לא תקין (מקסימום 11 ספרות)");
      return;
    }
    if (cleanPhone.length < 9) {
      setPhoneError("מספר טלפון קצר מדי");
      return;
    }

    if (!city || !salary || !welfareKnown) {
      return;
    }

    setLoading(true);

    try {
      const isoDate = new Date().toISOString().split("T")[0];
      const payload = {
        name: name.trim(),
        phone: cleanPhone,
        email: "",
        city,
        registerable_id: registerableId,
        registerable_type: registerableType || "Activity",
        registration_date: isoDate,
        known_in_welfare:
          welfareKnown === "כן" ? true : welfareKnown === "לא" ? false : null,
        salary_range: salary || null,
        unemployed_over_year: notWorking,
      };
      const res = await fetch("/api/registrations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        console.error("API error:", txt);
        throw new Error(txt);
      }
      setSubmitted(true);
    } catch (err) {
      console.error("Registration failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      dir="rtl"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl border-[3px] border-gray-900 p-6 z-10 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 border-2 border-gray-300 hover:bg-gray-200 transition-colors cursor-pointer"
          aria-label="סגור"
        >
          <i className="ri-close-line text-gray-700 text-lg"></i>
        </button>

        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 border-3 border-green-400">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-black text-gray-900">נרשמת בהצלחה!</h3>
            <p className="text-gray-500 text-sm">
              ניצור איתך קשר בקרוב. ברוך הבא למשפחת מתר!
            </p>
            <button
              onClick={onClose}
              className="mt-2 w-full bg-purple-400 border-2 border-gray-900 rounded-2xl py-3 text-base font-black text-gray-900 hover:bg-purple-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              סגור
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-purple-100 border-2 border-purple-300 mx-auto mb-3">
                <i className="ri-user-add-line text-2xl text-purple-600"></i>
              </div>
              <h2 className="text-2xl font-black text-gray-900">הצטרפו למתר</h2>
              <p className="text-sm text-gray-500 mt-1">
                מלאו את הפרטים ונחזור אליכם
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold text-gray-700">
                  שם מלא
                </label>
                <div className="relative">
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="ישראל ישראלי"
                    className="w-full h-13 bg-gray-50 border-2 border-gray-900 rounded-2xl pr-10 pl-4 py-3 text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold text-gray-700">
                  מספר טלפון
                </label>
                <div className="relative">
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="050-0000000"
                    className="w-full h-13 bg-gray-50 border-2 border-gray-900 rounded-2xl pr-10 pl-4 py-3 text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                    dir="ltr"
                  />
                </div>
                {phoneError && (
                  <p className="text-xs text-red-500 font-semibold mt-1">
                    {phoneError}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold text-gray-700">
                  {" "}
                  מוקד בו ארצה לקבל שירות?
                </label>
                <div className="relative">
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line text-gray-400 text-base"></i>
                  </div>
                  <select
                    name="city"
                    required
                    className="w-full h-13 bg-gray-50 border-2 border-gray-900 rounded-2xl pr-10 pl-4 py-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-purple-500 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="">בחר עיר...</option>
                    {cities.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400 text-base"></i>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold text-gray-700">
                  יש לי תיק פתוח ברווחה?
                </label>
                <div className="flex gap-3">
                  {["כן", "לא"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setWelfareKnown(opt)}
                      className="flex-1 py-2.5 rounded-2xl border-2 border-gray-900 text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                      style={{
                        background:
                          welfareKnown === opt ? "#A78BFA" : "#F9FAFB",
                        color: welfareKnown === opt ? "#1F2937" : "#6B7280",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold text-gray-700">
                  מהו טווח השכר שלי?
                </label>
                <div className="relative">
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-gray-400 text-base"></i>
                  </div>
                  <select
                    name="salary"
                    required
                    className="w-full h-13 bg-gray-50 border-2 border-gray-900 rounded-2xl pr-10 pl-4 py-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-purple-500 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="">בחר טווח...</option>
                    {[
                      "עד 5,000 ₪",
                      "5,000 - 8,000 ₪",
                      "8,000 - 12,000 ₪",
                      "12,000 - 18,000 ₪",
                      "18,000 - 25,000 ₪",
                      "מעל 25,000 ₪",
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400 text-base"></i>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between bg-gray-50 border-2 border-gray-900 rounded-2xl px-4 py-3">
                <span className="text-sm font-bold text-gray-700">
                  לא עבדתי בשנה האחרונה.
                </span>
                <button
                  type="button"
                  onClick={() => setNotWorking(!notWorking)}
                  className="relative w-12 h-6 rounded-full border-2 border-gray-900 transition-colors cursor-pointer"
                  style={{ background: notWorking ? "#A78BFA" : "#E5E7EB" }}
                >
                  <span
                    className="absolute top-0.5 w-4 h-4 rounded-full bg-white border border-gray-300 transition-all"
                    style={{
                      left: notWorking ? "2px" : "auto",
                      right: notWorking ? "auto" : "2px",
                    }}
                  ></span>
                </button>
              </div>
              <div className="bg-gray-50 border-2 border-gray-900 rounded-2xl p-4 space-y-3">
                {/* Checkbox row */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setChecked(!checked)}
                    className="w-5 h-5 mt-1 accent-black"
                  />

                  <span className="text-sm text-gray-700 font-medium leading-5">
                    אני מאשר/ת שקראתי ומסכימ/ה לתנאי השימוש והעברת המידע האישי
                  </span>
                </label>

                {/* text block */}
                <div className="text-xs text-gray-600 leading-5">
                  {showMore ? text : text.slice(0, 140) + "..."}
                </div>

                {/* show more */}
                <button
                  type="button"
                  onClick={() => setShowMore(!showMore)}
                  className="text-xs font-bold text-blue-600"
                >
                  {showMore ? "הסתר" : "הצג עוד"}
                </button>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-400 border-[3px] border-gray-900 rounded-2xl py-4 text-base font-black text-gray-900 hover:bg-purple-500 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap mt-2 disabled:opacity-60"
              >
                {loading ? (
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                ) : (
                  <>
                    <i className="ri-user-add-line text-lg"></i> הרשמה
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
